import type {
    CancelBody,
    CheckInBody,
    CheckoutBody,
    CourtSession,
    CourtSummary,
    JoinBody,
    QueueEntry,
    QueueListItem,
    RemoveQueueBody,
    UpdateCourtBody,
    UpdateCourtResponse,
} from "@saquei/types";

import { ApiError } from "./api";
import { randomUuid } from "./lib/uuid";

async function jsonOrThrow<T>(resp: Response): Promise<T> {
    if (!resp.ok) {
        let msg = `HTTP ${resp.status}`;
        try {
            const body = await resp.json();
            if (body?.error?.message) msg = String(body.error.message);
        } catch {
            // ignore
        }
        throw new ApiError(resp.status, msg);
    }
    if (resp.status === 204) return undefined as unknown as T;
    return (await resp.json()) as T;
}

function authHeaders(token: string): HeadersInit {
    return {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
    };
}

function withIdempotency(headers: HeadersInit): HeadersInit {
    return {
        ...headers,
        "idempotency-key": randomUuid(),
    };
}

// ==========================================================================
// Courts (read)
// ==========================================================================

export async function listCourts(token: string): Promise<CourtSummary[]> {
    const resp = await fetch("/api/v1/courts", {
        method: "GET",
        headers: authHeaders(token),
    });
    return jsonOrThrow<CourtSummary[]>(resp);
}

export async function getCourtByCode(
    token: string,
    shortCode: string,
): Promise<CourtSummary> {
    const resp = await fetch(`/api/v1/q/${encodeURIComponent(shortCode)}`, {
        method: "GET",
        headers: authHeaders(token),
    });
    return jsonOrThrow<CourtSummary>(resp);
}

// ==========================================================================
// Courts (mutating — idempotency required)
// ==========================================================================

export async function checkIn(
    token: string,
    shortCode: string,
    body: CheckInBody,
): Promise<CourtSession> {
    const resp = await fetch(`/api/v1/q/${encodeURIComponent(shortCode)}/checkin`, {
        method: "POST",
        headers: withIdempotency(authHeaders(token)),
        body: JSON.stringify(body),
    });
    return jsonOrThrow<CourtSession>(resp);
}

export async function denounce(token: string, shortCode: string): Promise<CourtSession> {
    const resp = await fetch(`/api/v1/q/${encodeURIComponent(shortCode)}/denounce`, {
        method: "POST",
        headers: withIdempotency(authHeaders(token)),
        body: "",
    });
    return jsonOrThrow<CourtSession>(resp);
}

export async function checkout(
    token: string,
    sessionId: string,
    body: CheckoutBody = {},
): Promise<CourtSession> {
    const resp = await fetch(`/api/v1/sessions/${sessionId}/checkout`, {
        method: "POST",
        headers: withIdempotency(authHeaders(token)),
        body: JSON.stringify(body),
    });
    return jsonOrThrow<CourtSession>(resp);
}

export async function extend(
    token: string,
    sessionId: string,
): Promise<CourtSession> {
    const resp = await fetch(`/api/v1/sessions/${sessionId}/extend`, {
        method: "POST",
        headers: withIdempotency(authHeaders(token)),
        body: "",
    });
    return jsonOrThrow<CourtSession>(resp);
}

// ==========================================================================
// Queue
// ==========================================================================

export async function joinQueue(token: string, body: JoinBody): Promise<QueueEntry> {
    const resp = await fetch("/api/v1/queue", {
        method: "POST",
        headers: withIdempotency(authHeaders(token)),
        body: JSON.stringify(body),
    });
    return jsonOrThrow<QueueEntry>(resp);
}

export async function listQueue(token: string): Promise<QueueListItem[]> {
    const resp = await fetch("/api/v1/queue", {
        method: "GET",
        headers: authHeaders(token),
    });
    return jsonOrThrow<QueueListItem[]>(resp);
}

export async function cancelQueueEntry(
    token: string,
    entryId: string,
    body: CancelBody = {},
): Promise<QueueEntry> {
    const resp = await fetch(`/api/v1/queue/${entryId}`, {
        method: "DELETE",
        headers: withIdempotency(authHeaders(token)),
        body: JSON.stringify(body),
    });
    return jsonOrThrow<QueueEntry>(resp);
}

export async function passQueueEntry(token: string, entryId: string): Promise<QueueEntry> {
    const resp = await fetch(`/api/v1/queue/${entryId}/pass`, {
        method: "POST",
        headers: withIdempotency(authHeaders(token)),
        body: "",
    });
    return jsonOrThrow<QueueEntry>(resp);
}

// ==========================================================================
// Admin
// ==========================================================================

export async function adminForceEndSession(
    token: string,
    sessionId: string,
): Promise<CourtSession> {
    const resp = await fetch(`/api/v1/admin/sessions/${sessionId}/force-end`, {
        method: "POST",
        headers: withIdempotency(authHeaders(token)),
        body: "",
    });
    return jsonOrThrow<CourtSession>(resp);
}

export async function adminRemoveQueueEntry(
    token: string,
    entryId: string,
    body: RemoveQueueBody = {},
): Promise<QueueEntry> {
    const resp = await fetch(`/api/v1/admin/queue/${entryId}/remove`, {
        method: "DELETE",
        headers: withIdempotency(authHeaders(token)),
        body: JSON.stringify(body),
    });
    return jsonOrThrow<QueueEntry>(resp);
}

export async function adminUpdateCourtStatus(
    token: string,
    courtId: string,
    body: UpdateCourtBody,
): Promise<UpdateCourtResponse> {
    const resp = await fetch(`/api/v1/admin/courts/${courtId}`, {
        method: "PATCH",
        headers: withIdempotency(authHeaders(token)),
        body: JSON.stringify(body),
    });
    return jsonOrThrow<UpdateCourtResponse>(resp);
}
